import type { CustomToolKitParam } from "../ToolConfig.ts";
import type { BlocklyBotDropdownData } from "./bot-data.ts";

export type BlocklyDropdownOption = [string, string];
export type BlocklyTemplateVarType = string;

export interface BlocklyTemplateVar {
  name: string;
  type: BlocklyTemplateVarType;
  value?: unknown;
}

export interface BlocklyIfthenEditorItem {
  id: string;
  uniqueId: string;
  name?: string;
}

export interface BlocklyIfthenToolConfig {
  id: string;
  skillId: string;
  enabled: boolean;
  name?: string;
  params: CustomToolKitParam[];
}

/**
 * Synchronous data boundary for shared Blockly definitions.
 *
 * | Member | Current admin source |
 * | --- | --- |
 * | getImageSkills() | RouteShareService._allImageSkills() |
 * | getDocuments() | RouteShareService._allDokuments() |
 * | getWebhooks() | RouteShareService._allWebhooks() |
 * | getDataBases() | RouteShareService._allDataBases() |
 * | getWennDann() | RouteShareService._allWennDann() |
 * | getOnlineSources() | RouteShareService._allOnlineSources() |
 * | getOfflineWebsites() | RouteShareService._allOfflineWebsites() |
 * | getFusionViews() | RouteShareService._allFusionViews() |
 * | getFusionData() | RouteShareService._allFusionData() |
 * | getStages() | RouteShareService._allStages() |
 * | getSkills() | RouteShareService._allSkills() |
 * | getTemplateVars() | RouteShareService.botConfig.templateVars |
 * | getBotName() | RouteShareService.botConfig.name |
 * | getBotModel() | RouteShareService.botConfig.model |
 * | getBotTemperature() | RouteShareService.botConfig.temperature |
 * | getIfthenEditorItems() | RouteShareService.editorItems.ifthen |
 * | getCurrentEditorItemUniqueId() | RouteShareService.editorItem.uniqueId |
 * | getIfthenToolConfigs() | RouteShareService.botConfig.toolConfigs customtoolkit entries |
 * | getProjectSecrets() | ProjectSecretService.projectSecrets[].name |
 * | getEventStrings() | EventsStringService.get() |
 * | getRawEventStrings() | EventsStringService.getRaw() |
 * | getEventIds() | EventIdRegistryService.getDropdownOptions() |
 * | getDialogRecorderIds() | DialogRecorderIdRegistryService.getDropdownOptions() |
 * | getGroupchatChannels() | GroupchatChannelRegistryService.getDropdownOptions() |
 *
 * Server implementations must provide the same option values without Angular services:
 * event IDs come from scanning every stored Blockly XML for EVENTS_ON_NEW eventId
 * fields, dialog recorder IDs from scanning DIALOGRECORDER_start_NEW recorderId
 * fields, and groupchat channels from persisted project key-value entries whose
 * type uses GROUPCHAT_TYPE_PREFIX plus the default Gruppenchat channel. Stack
 * dropdown data mirrors the bot/project resource lists used by RouteShareService.
 */
export interface BlocklyDataProvider {
  getImageSkills(): BlocklyBotDropdownData["allImageSkills"];
  getDocuments(): BlocklyBotDropdownData["allDokuments"];
  getWebhooks(): BlocklyBotDropdownData["allWebhooks"];
  getDataBases(): BlocklyBotDropdownData["allDataBases"];
  getWennDann(): BlocklyBotDropdownData["allWennDann"];
  getOnlineSources(): BlocklyBotDropdownData["allOnlineSources"];
  getOfflineWebsites(): BlocklyBotDropdownData["allOfflineWebsites"];
  getFusionViews(): BlocklyBotDropdownData["allFusionViews"];
  getFusionData(): BlocklyBotDropdownData["allFusionData"];
  getStages(): BlocklyBotDropdownData["allStages"];
  getSkills(): BlocklyBotDropdownData["allSkills"];
  getTemplateVars(type?: BlocklyTemplateVarType): BlocklyTemplateVar[];
  getBotName(): string;
  getBotModel(): string;
  getBotTemperature(): number | null;
  getIfthenEditorItems(): BlocklyIfthenEditorItem[];
  getCurrentEditorItemUniqueId(): string | null;
  getIfthenToolConfigs(): BlocklyIfthenToolConfig[];
  getProjectSecrets(): string[];
  getEventStrings(startsWith?: string): BlocklyDropdownOption[];
  getRawEventStrings(startsWith?: string): BlocklyDropdownOption[];
  getEventIds(): BlocklyDropdownOption[];
  getDialogRecorderIds(): BlocklyDropdownOption[];
  getGroupchatChannels(): BlocklyDropdownOption[];
}

const EMPTY_DROPDOWN: BlocklyDropdownOption[] = [];

export const EmptyBlocklyDataProvider: BlocklyDataProvider = {
  getImageSkills: () => [],
  getDocuments: () => [],
  getWebhooks: () => [],
  getDataBases: () => [],
  getWennDann: () => [],
  getOnlineSources: () => [],
  getOfflineWebsites: () => [],
  getFusionViews: () => [],
  getFusionData: () => [],
  getStages: () => [],
  getSkills: () => [],
  getTemplateVars: () => [],
  getBotName: () => "",
  getBotModel: () => "",
  getBotTemperature: () => null,
  getIfthenEditorItems: () => [],
  getCurrentEditorItemUniqueId: () => null,
  getIfthenToolConfigs: () => [],
  getProjectSecrets: () => [],
  getEventStrings: () => EMPTY_DROPDOWN,
  getRawEventStrings: () => EMPTY_DROPDOWN,
  getEventIds: () => EMPTY_DROPDOWN,
  getDialogRecorderIds: () => EMPTY_DROPDOWN,
  getGroupchatChannels: () => EMPTY_DROPDOWN,
};

let currentBlocklyDataProvider: BlocklyDataProvider = EmptyBlocklyDataProvider;

export function setBlocklyDataProvider(
  provider: BlocklyDataProvider | null | undefined,
): void {
  currentBlocklyDataProvider = provider ?? EmptyBlocklyDataProvider;
}

export function getBlocklyDataProvider(): BlocklyDataProvider {
  return currentBlocklyDataProvider;
}
